import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ASSETS, CONTACT } from "../lib/brand";

const APPEARANCES = [
  { outlet: "Gazeta do Povo", role: "Reportagem" },
  { outlet: "Bem Paraná", role: "Entrevista" },
  { outlet: "TV Paraná", role: "Programa ao vivo" },
  { outlet: "Rádio Massa", role: "Coluna" },
  { outlet: "Revista TOP", role: "Perfil editorial" },
  { outlet: "BNI Brasil", role: "Palestrante" },
];

export default function AoVivo() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-10% 0px" });

  return (
    <section
      id="ao-vivo"
      ref={ref}
      data-testid="ao-vivo-section"
      className="relative bg-cream-100 py-24 sm:py-40 border-t border-ink-800/15"
    >
      <div className="mx-auto max-w-[1600px] px-6 sm:px-10 grid grid-cols-12 gap-6 lg:gap-16 items-start">
        {/* Left: title & meta */}
        <div className="col-span-12 lg:col-span-6">
          <div className="flex items-center gap-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60 mb-8">
            <span className="w-8 h-px bg-brick-500" />
            <span>N.º 003 — Herida ao vivo</span>
            <span className="hidden sm:inline-flex items-center gap-2 text-brick-500">
              <span className="w-1.5 h-1.5 bg-brick-500 rounded-full animate-pulse" />
              on air
            </span>
          </div>

          <div className="overflow-hidden">
            <motion.h2
              initial={{ y: "110%" }}
              animate={inView ? { y: 0 } : {}}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif italic font-medium text-brick-500 leading-[0.88] tracking-tight"
              style={{ fontSize: "clamp(3.5rem, 9vw, 9.5rem)" }}
            >
              Herida
            </motion.h2>
          </div>
          <div className="overflow-hidden mt-1">
            <motion.h2
              initial={{ y: "110%" }}
              animate={inView ? { y: 0 } : {}}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
              className="font-serif text-ink-800 leading-[0.88] tracking-tight"
              style={{ fontSize: "clamp(3.5rem, 9vw, 9.5rem)" }}
            >
              ao vivo.
            </motion.h2>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay: 0.55 }}
            className="mt-10 font-sans text-base sm:text-lg leading-relaxed text-ink-800/70 max-w-lg"
          >
            Entrevistas na rádio, presença na TV, colunas em revistas, painéis e talks.
            Uma seleção viva dos momentos em que ocupei o microfone e conduzi a
            conversa sobre moda circular.
          </motion.p>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.75 }}
            className="mt-12 divide-y divide-ink-800/15 border-t border-b border-ink-800/15"
            data-testid="ao-vivo-appearances"
          >
            {APPEARANCES.map((a, i) => (
              <motion.li
                key={a.outlet}
                initial={{ opacity: 0, x: -10 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.85 + i * 0.06 }}
                className="flex items-baseline justify-between py-4"
              >
                <span className="font-serif italic text-xl sm:text-2xl text-ink-800">
                  {a.outlet}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-800/60">
                  {a.role}
                </span>
              </motion.li>
            ))}
          </motion.ul>
        </div>

        {/* Right: video card */}
        <div className="col-span-12 lg:col-span-6 lg:pl-8 lg:mt-6">
          <motion.a
            href={CONTACT.aoVivo}
            target="_blank"
            rel="noreferrer"
            aria-label="Assistir Herida ao vivo no Instagram"
            data-testid="ao-vivo-cta"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -6 }}
            className="group relative block bg-ink-800 text-cream border border-ink-800 overflow-hidden"
          >
            <div className="relative aspect-[4/5] overflow-hidden bg-ink-700">
              <img
                src={ASSETS.phone}
                alt="Prévia — Herida ao vivo"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105 opacity-90"
              />
              <div
                aria-hidden
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(26,24,24,0.15) 30%, rgba(26,24,24,0.7) 100%)",
                }}
              />

              {/* Play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative w-24 h-24 sm:w-28 sm:h-28 flex items-center justify-center">
                  <motion.span
                    aria-hidden
                    className="absolute inset-0 rounded-full border border-cream"
                    animate={{ scale: [1, 1.35, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut" }}
                  />
                  <motion.span
                    aria-hidden
                    className="absolute inset-0 rounded-full border border-cream/60"
                    animate={{ scale: [1, 1.6, 1], opacity: [0.4, 0, 0.4] }}
                    transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: 0.6 }}
                  />
                  <span className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-brick-500 text-cream flex items-center justify-center group-hover:bg-cream group-hover:text-brick-500 transition-colors duration-500">
                    <svg
                      viewBox="0 0 24 24"
                      width="26"
                      height="26"
                      fill="currentColor"
                      aria-hidden="true"
                      className="ml-1"
                    >
                      <path d="M8 5.14v13.72a1 1 0 001.55.83l10.03-6.86a1 1 0 000-1.66L9.55 4.31A1 1 0 008 5.14z" />
                    </svg>
                  </span>
                </div>
              </div>

              <div className="absolute top-3 left-3 right-3 flex justify-between font-mono text-[9px] uppercase tracking-[0.3em] text-cream">
                <span className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-brick-500 rounded-full animate-pulse" />
                  Ao vivo · imprensa
                </span>
                <span>HL / Mídia</span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 px-5 sm:px-6 py-5 border-t border-cream/15">
              <div className="flex flex-col">
                <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-brick-500">
                  Stories · destacados
                </span>
                <span
                  className="mt-1 font-serif italic leading-none text-cream"
                  style={{ fontSize: "clamp(1.35rem, 1.7vw, 1.75rem)" }}
                >
                  Assistir Herida ao vivo
                </span>
              </div>
              <span className="font-serif italic text-3xl text-brick-500 group-hover:translate-x-1 transition-transform duration-500">
                →
              </span>
            </div>
          </motion.a>
        </div>
      </div>
    </section>
  );
}
